<template>
  <div class="form" :class="nftLayout">
    <div class="form__content">
      <h1 class="extra-large-headline">Add billing details</h1>
    </div>
    <form @submit.prevent>
      <div class="form__content-wrapper">
        <FormInputField
          name="givenName"
          label="First Name*"
          placeholder="Enter your first name"
          type="text"
          :inputValue="form.givenName"
          :errors="store.getErrorByField('givenName')"
          v-model:givenName="form.givenName"
        />
        <FormInputField
          name="middleName"
          label="Middle Name"
          placeholder="Enter your middle name"
          type="text"
          :inputValue="form.middleName"
          v-model:middleName="form.middleName"
          v-if="!orderStore.isNFTMode"
        />
        <FormInputField
          name="surname"
          label="Last Name*"
          placeholder="Enter your last name"
          type="text"
          :inputValue="form.surname"
          v-model:surname="form.surname"
        />
        <FormInputField
          name="dob"
          label="Date of Birth*"
          placeholder="DD-MM-YYYY"
          type="text"
          mask="##-##-####"
          :inputValue="form.dob"
          :errors="store.getErrorByField('dob')"
          v-model:dob="form.dob"
        />

        <FormInputField
          class="form__addressLine1"
          name="addressLine1"
          label="Residential Address*"
          placeholder="Current residential address"
          type="text"
          iconType="MapPinCircle"
          :inputValue="form.addressLine1"
          v-model:addressLine1="form.addressLine1"
        />

        <FormInputField
          name="suburb"
          label="City / Suburb / Town*"
          placeholder="Your city"
          type="text"
          :inputValue="form.suburb"
          v-model:suburb="form.suburb"
        />

        <!-- may need to revist on more complex country requirements -->
        <FormSelect
          v-if="showFieldsOnUSIdentity"
          name="usStates"
          label="Select a state"
          type="text"
          placeholder="Your state"
          :items="createUSStateItemList"
          @dropdownValueUpdate="updateDropdown"
        />

        <FormInputField
          v-else
          name="state"
          label="State / County / Province*"
          placeholder="Your state"
          type="text"
          :inputValue="form.state"
          v-model:state="form.state"
        />

        <FormInputField
          v-if="showFieldsOnUSIdentity"
          name="taxId"
          label="SSN*"
          placeholder="###-##-####"
          type="text"
          mask="###-##-####"
          :inputValue="form.taxId"
          :errors="store.getErrorByField('taxId')"
          v-model:taxId="form.taxId"
        />
        <FormInputField
          name="postcode"
          label="Post Code*"
          placeholder="Your post code"
          type="text"
          :inputValue="form.postcode"
          v-model:postcode="form.postcode"
        />
        <AutocompleteSelect
          hasIcon
          v-model:autocomplete="form.country"
          :label="'Country of residence*'"
          :placeholder="'Search a country'"
          :type="'text'"
          :items="countryList"
          :isLoading="isFetchingData"
        />

        <OccupationField
          v-if="showFieldsOnCAIdentity"
          name="occupation"
          @inputValueUpdate="updateOccupation"
        />

        <Checkbox
          v-if="showFieldsOnUSIdentity"
          name="tnc"
          text="I agree to the <a target='_blank' href='https://www.zerohash.com/banxa'>Zero Hash and Zero Hash Liquidity Services User Agreement</a>, and I have read and understand the Zero Hash <a target='_blank' href='https://seedcx.zendesk.com/hc/en-us/articles/360009900494-Privacy-Policy-'>Privacy Policy</a> and <a href='https://seedcx.zendesk.com/hc/en-us/articles/360008819314-State-Money-Transmitter-Disclosures' target='_blank'>Regulatory Disclosures</a>. I understand that the value of any cryptocurrency, including digital assets pegged to fiat currency, commodities, or any other asset, may go to zero."
          v-model:value="form.tnc"
          v-model:tnc="form.tnc"
          :errors="store.getErrorByField('tnc')"
        />
      </div>

      <ButtonBase
        :isDisabled="store.formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Submit my details
      </ButtonBase>
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { useIdentityConfirmationStore } from "@/stores/forms/identityConfirmationFormStore";
import { useOccupationStore } from "@/stores/forms/occupationFormStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import useApiHelpers from "@/composables/api/helpers";
import useSettingsEndpoints from "@/composables/api/useSettingsEndpoints";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useIdentityCountryMixin from "@/composables/watchers/useIdentityCountryMixin";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";
import useCustomerActions from "@/composables/api/useCustomerActions";

import FormInputField from "@/components/ui/inputs/FormInputField.vue";
import FormSelect from "@/components/ui/selects/FormSelect.vue";
import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import OccupationField from "@/components/ui/inputs/OccupationField.vue";

const globalStore = useGlobalStore();
const verificationStore = useVerificationStore();
const store = useIdentityConfirmationStore();
const identityQueueStore = useIdentityQueueStore();
const orderStore = useOrderFormStore();

const { postRequest } = useApiHelpers();
const { getRestrictedUSStatesSettings } = useSettingsEndpoints();
const router = useRouter();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { watchCountryAndUpdateStoreHandler } = useIdentityCountryMixin();
const { fetchCountries } = useGlobalCountriesActions();
const { fetchCustomerKycSharingInfo, fillForm } = useCustomerActions();

const countryList = ref([]);
const stateList = ref([]);
const disabledStatesList = ref([]);
const form = ref({
  givenName: store.form.givenName.value || "",
  middleName: store.form.middleName.value || "",
  surname: store.form.surname.value || "",
  dob: store.form.dob.value || "",
  addressLine1: store.form.addressLine1.value || "",
  suburb: store.form.suburb.value || "",
  state: store.form.state.value || "",
  postcode: store.form.postcode.value || "",
  taxId: store.form.taxId.value || "",
  tnc: false,
  occupation: store.form.occupation.value || "",
  country: verificationStore.form.country.value || "",
});
const isFetchingData = ref(true);
const nftLayout = computed(() => {
  return orderStore.isNFTMode ? "nft-layout" : "";
});
const getCountryRelatedData = async () => {
  const {
    data: { acceptedStates, restrictedStates },
  } = await getRestrictedUSStatesSettings();

  const restrictedStateCodes = Object.keys(restrictedStates);

  stateList.value = {
    ...acceptedStates,
    ...restrictedStates,
  };
  disabledStatesList.value = restrictedStateCodes;
};

const showFieldsOnUSIdentity = computed(
  () =>
    form.value.country && form.value.country.toLowerCase() === "united states"
);

const showFieldsOnCAIdentity = computed(
  () => form.value.country && form.value.country.toLowerCase() === "canada"
);

const createUSStateItemList = computed(() => {
  const states = Object.entries(stateList.value);
  if (states.length === 0) return [];
  return states.map(([stateCode, stateName]) => {
    return {
      name: stateName,
      value: stateCode,
      isDisabled: disabledStatesList.value.includes(stateCode),
    };
  });
});

const updateDropdown = (dropdownData) => {
  form.value.state = Object.values(dropdownData)[0]?.value;
};

const updateOccupation = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  form.value[field] = value;
};

const handleFormSuccess = () => {
  router.push(identityQueueStore.getNextIdentityRoute);
};

const handleFormSubmit = () => {
  const { country, ...rest } = form.value;
  const countryCode = globalStore.getCountryCode(country);
  globalStore.setIsFormLoading(true);
  if (countryCode !== "US") {
    postRequest(
      "customers",
      { country: countryCode, ...rest },
      handleFormSuccess
    );
    return;
  }
  // Post taxState for US customer
  postRequest(
    "customers",
    { country: countryCode, taxState: form.value.state, ...rest },
    handleFormSuccess
  );
};

onMounted(async () => {
  await getCountryRelatedData();
  const formattedCountries = await fetchCountries();

  countryList.value = formattedCountries;

  const personalDetails = await fetchCustomerKycSharingInfo();

  await fillForm(personalDetails, form, store);

  isFetchingData.value = false;
});

watchAndUpdateStore(form, store, () => {});
watchCountryAndUpdateStoreHandler(form, store, () => {});
</script>

<style lang="scss" scoped>
.form {
  &__content-wrapper {
    display: flex;
    flex-wrap: wrap;

    .base-input,
    .occupation-field,
    .base-form-select,
    .autocomplete-select,
    .base-field {
      flex-grow: 1;
      padding-inline: 0.5rem;

      @include breakpoint($min-l) {
        &:not(.form__addressLine1) {
          width: 50%;
        }
      }
    }
  }
}

.nft-layout {
  .base-input,
  .occupation-field,
  .base-form-select,
  .autocomplete-select,
  .base-field {
    &:not(.form__addressLine1) {
      width: 50%;
    }
  }
}
</style>
